import React, { useState, useEffect, useCallback } from 'react';
import Form from 'react-bootstrap/Form';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { Api } from './interface';
import { CAPITAL_ASSET_FIELDS } from './constants';
import { getFieldValue, updateFieldValue } from './common'; // Import the utility functions
import Modal from 'react-bootstrap/Modal';
import { Button, ModalBody, ModalFooter } from 'react-bootstrap';

const CapitalAssetSelector = ({
  onCapitalAssetChange,
  initialData,
  parent,
  readOnlyPOIO,
  readOnlyCtag,
  errorModal,
  closeErrorPopup,
  skipCapitalUpdate,
}) => {
  const [capitalAsset, setCapitalAsset] = useState({
    isCapitalAsset: initialData.isCapitalAsset || false,
    poNumbers: [...initialData.poNumbers] || [],
    ioNumbers: [...initialData.ioNumbers] || [],
    isLoadingPOs: false,
    isLoadingIOs: false,
  });
  const [capitalFields, setCapitalFields] = useState(
    [...initialData.capitalFields] || []
  );

  // Load PO numbers on component mount if not already loaded in initial data
  // Load capital fields on component mount if not already loaded in initial data
  useEffect(() => {
    if (initialData?.poNumbers.length == 0) {
      loadPONumbers();
    }

    if (initialData?.capitalFields?.length < 3) {
      mergeCapitalFields(); //merge capital fileds
    }
  }, []);

  useEffect(() => {
    if (onCapitalAssetChange) {
      onCapitalAssetChange({
        isCapitalAsset: capitalAsset.isCapitalAsset,
        capitalFields: capitalFields,
        poNumbers: capitalAsset.poNumbers,
        ioNumbers: capitalAsset.ioNumbers,
      });
    }
  }, [
    capitalAsset.isCapitalAsset,
    capitalFields,
    capitalAsset.ioNumbers,
    capitalAsset.poNumbers,
  ]);

  const mergeCapitalFields = useCallback(() => {
    Api({
      sp: 'getCapitalAssetFields',
      json: {},
    }).then((fields) => {
      let initalFields = [];
      initalFields = initialData.capitalFields;

      // Merge initial capital fields with fetched fields to retain any existing values
      let ctag = getFieldValue(initalFields, CAPITAL_ASSET_FIELDS.C_TAG);
      let poNumber = getFieldValue(
        initalFields,
        CAPITAL_ASSET_FIELDS.PO_NUMBER
      );
      let ioNumber = getFieldValue(
        initalFields,
        CAPITAL_ASSET_FIELDS.IO_NUMBER
      );

      if (!ctag) {
        initalFields.push({
          ...fields.find(
            (f) => f.EntityMetaType === CAPITAL_ASSET_FIELDS.C_TAG
          ),
        });
      }
      if (!poNumber) {
        initalFields.push({
          ...fields.find(
            (f) => f.EntityMetaType === CAPITAL_ASSET_FIELDS.PO_NUMBER
          ),
        });
      }
      if (!ioNumber) {
        initalFields.push({
          ...fields.find(
            (f) => f.EntityMetaType === CAPITAL_ASSET_FIELDS.IO_NUMBER
          ),
        });
      }

      setCapitalFields(initalFields || []);
    });
  }, []);

  const loadPONumbers = useCallback(() => {
    setCapitalAsset((prevState) => ({
      ...prevState,
      isLoadingPOs: true,
    }));

    Api({
      sp: 'getPurchaseOrders',
      json: {},
    })
      .then((poNumbers) => {
        setCapitalAsset((prevState) => ({
          ...prevState,
          poNumbers: poNumbers || [],
          isLoadingPOs: false,
        }));
      })
      .catch((error) => {
        console.error('Error loading PO numbers:', error);
        setCapitalAsset((prevState) => ({
          ...prevState,
          poNumbers: [],
          isLoadingPOs: false,
        }));
      });
  }, []);

  // Load IO numbers when PO is restored from initial data
  useEffect(() => {
    if (initialData?.capitalFields?.length > 0) {
      const selectedPONumber = getFieldValue(
        initialData.capitalFields,
        CAPITAL_ASSET_FIELDS.PO_NUMBER
      );

      if (selectedPONumber && initialData.ioNumbers.length === 0) {
        loadIONumbersForPO(selectedPONumber);
      }
    }
  }, []);

  const loadIONumbersForPO = useCallback((poNumber) => {
    if (!poNumber) {
      setCapitalAsset((prevState) => ({
        ...prevState,
        ioNumbers: [],
      }));
      return;
    }

    setCapitalAsset((prevState) => ({
      ...prevState,
      isLoadingIOs: true,
    }));

    Api({
      sp: 'getInternalOrders',
      json: {
        po_dbid: poNumber.po_dbid,
      },
    })
      .then((ioNumbers) => {
        setCapitalAsset((prevState) => ({
          ...prevState,
          ioNumbers: ioNumbers || [],
          isLoadingIOs: false,
        }));
      })
      .catch((error) => {
        console.error('Error loading IO numbers:', error);
        setCapitalAsset((prevState) => ({
          ...prevState,
          ioNumbers: [],
          isLoadingIOs: false,
        }));
      });
  }, []);

  const handleCapitalAssetToggle = useCallback(
    (isChecked) => {
      if (readOnlyCtag || readOnlyPOIO) return; // Prevent toggle when in read-only mode
      setCapitalAsset((prevState) => ({
        ...prevState,
        isCapitalAsset: isChecked,
        ioNumbers: [],
      }));

      // Clear all capital fields when toggling off
      if (!isChecked) {
        setCapitalFields((prevFields) =>
          prevFields.map((field) => ({
            ...field,
            EntityMetaValue: null,
          }))
        );
      }
    },
    [readOnlyCtag, readOnlyPOIO]
  );

  const handlePOSelection = useCallback(
    (newPO) => {
      if (readOnlyPOIO) return; // Prevent changes when in read-only mode
      // Update selectedPONumber field using common utility
      setCapitalFields((prevFields) =>
        updateFieldValue(prevFields, CAPITAL_ASSET_FIELDS.PO_NUMBER, newPO)
      );

      // Reset IO selection when PO changes
      setCapitalFields((prevFields) =>
        updateFieldValue(prevFields, CAPITAL_ASSET_FIELDS.IO_NUMBER, null)
      );

      if (newPO) {
        loadIONumbersForPO(newPO);
      } else {
        // Clear IO numbers if no PO selected
        setCapitalAsset((prevState) => ({
          ...prevState,
          ioNumbers: [],
        }));
      }
    },
    [loadIONumbersForPO, readOnlyPOIO]
  );

  const handleIOSelection = useCallback(
    (newIO) => {
      if (readOnlyPOIO) return; // Prevent changes when in read-only mode
      setCapitalFields((prevFields) =>
        updateFieldValue(prevFields, CAPITAL_ASSET_FIELDS.IO_NUMBER, newIO)
      );
    },
    [readOnlyPOIO]
  );

  const handleCtagChange = useCallback(
    (value) => {
      if (readOnlyCtag) return;
      setCapitalFields((prevFields) =>
        updateFieldValue(prevFields, CAPITAL_ASSET_FIELDS.C_TAG, value)
      );
    },
    [readOnlyCtag]
  );

  // Filter options for PO autocomplete
  const filterPOOptions = useCallback((options, { inputValue }) => {
    if (!inputValue) return options;
    const searchTerm = inputValue.toLowerCase();

    return options.filter((option) => {
      const poNum = String(option.po_num || '').toLowerCase();
      const poDesc = String(option.pi_description || '').toLowerCase();

      return poNum.includes(searchTerm) || poDesc.includes(searchTerm);
    });
  }, []);

  // Filter options for IO autocomplete
  const filterIOOptions = useCallback((options, { inputValue }) => {
    if (!inputValue) return options;
    const searchTerm = inputValue.toLowerCase();
    return options.filter((option) => {
      const ioNum = String(option.io_num || '').toLowerCase();
      const ioDesc = String(option.io_description || '').toLowerCase();
      return ioNum.includes(searchTerm) || ioDesc.includes(searchTerm);
    });
  }, []);

  // Get current field values using the common utility
  const selectedPONumber = getFieldValue(
    capitalFields,
    CAPITAL_ASSET_FIELDS.PO_NUMBER
  );
  const selectedIONumber = getFieldValue(
    capitalFields,
    CAPITAL_ASSET_FIELDS.IO_NUMBER
  );
  const ctag = getFieldValue(capitalFields, CAPITAL_ASSET_FIELDS.C_TAG) || '';

  // Check if all required fields are filled
  const areRequiredFieldsFilled = selectedPONumber && selectedIONumber && ctag;

  const noOptionsTextPO = readOnlyPOIO
    ? selectedPONumber
    : 'No PO numbers available';

  const noOptionsTextIO = readOnlyPOIO
    ? selectedIONumber
    : 'No IO numbers found for this PO';

  const viewPONumber =
    parent === 'entityEdit' ? true : capitalAsset.isCapitalAsset;

  const viewIONumber =
    parent === 'entityEdit'
      ? true
      : capitalAsset.isCapitalAsset && selectedPONumber;

  const viewCtag =
    parent === 'entityEdit'
      ? true
      : capitalAsset.isCapitalAsset && selectedIONumber;

  return (
    <>
      {/* Capital Asset Toggle with Info Popup */}
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <label
            htmlFor="capital-asset-toggle"
            className="form-label mb-0 me-2"
          >
            Is your asset a capital asset?
          </label>

          <Tooltip
            title={
              <div>
                <ul className="add-entity-tooltip-list">
                  <li>
                    Any asset with total costs greater than or equal to $5,000
                    and is expected to be used or potentially usable for at
                    least two years or more
                  </li>
                  <li>Examples: Equipment, machinery, vehicles</li>
                  <li>Requires PO Number, IO Number, and Ctag for tracking</li>
                </ul>
              </div>
            }
            arrow
            placement="top"
            enterDelay={300}
            leaveDelay={200}
          >
            <InfoIcon
              color="primary"
              className="add-entity-info-icon"
              tabIndex={0}
              role="button"
              aria-label="Capital asset information"
            />
          </Tooltip>
        </div>
        <Form.Check
          type="switch"
          id="capital-asset-toggle"
          checked={capitalAsset.isCapitalAsset}
          onChange={(e) => handleCapitalAssetToggle(e.target.checked)}
          disabled={readOnlyPOIO || readOnlyCtag}
        />
      </div>

      {/* PO Number Dropdown */}
      {viewPONumber && (
        <div className="mb-3">
          {CAPITAL_ASSET_FIELDS.PO_NUMBER}{' '}
          <span style={{ color: 'red' }}>*</span>
          <Autocomplete
            autoHighlight={true}
            className="type-ahead"
            id="po-number"
            options={capitalAsset.poNumbers}
            getOptionLabel={(option) => (option ? `${option?.po_num}` : '')}
            renderOption={(props, option) => (
              <li {...props} key={option?.pi_dbid}>
                <div>
                  <div className="fw-bold">{option?.po_num}</div>
                  <div className="add-entity-option-io-po">
                    {option?.pi_description}
                  </div>
                </div>
              </li>
            )}
            value={selectedPONumber}
            onChange={(_event, newPO) => handlePOSelection(newPO)}
            loading={capitalAsset.isLoadingPOs}
            loadingText="Loading PO Numbers..."
            noOptionsText={noOptionsTextPO}
            disabled={readOnlyPOIO || !capitalAsset.isCapitalAsset}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Search By PO Number or PO Item Text"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {capitalAsset.isLoadingPOs && (
                        <div className="add-entity-endAdornment-wrapper">
                          <span className="add-entity-endAdornment">
                            Loading...
                          </span>
                        </div>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            filterOptions={filterPOOptions}
            freeSolo={false}
          />
        </div>
      )}

      {/* IO Number Dropdown */}
      {viewIONumber && (
        <div className="mb-3">
          {CAPITAL_ASSET_FIELDS.IO_NUMBER}{' '}
          <span style={{ color: 'red' }}>*</span>
          <Autocomplete
            autoHighlight={true}
            className="type-ahead"
            id="io-number"
            options={capitalAsset.ioNumbers}
            getOptionLabel={(option) => (option ? `${option?.io_num}` : '')}
            renderOption={(props, option) => (
              <li {...props}>
                <div>
                  <div className="fw-bold">{option?.io_num}</div>
                  <div className="add-entity-option-io-po">
                    {option?.io_description}
                  </div>
                </div>
              </li>
            )}
            value={selectedIONumber}
            onChange={(_event, newIO) => handleIOSelection(newIO)}
            loading={capitalAsset.isLoadingIOs}
            loadingText="Loading IO Numbers..."
            noOptionsText={noOptionsTextIO}
            disabled={readOnlyPOIO || !capitalAsset.isCapitalAsset}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select IO Number"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {capitalAsset.isLoadingIOs && (
                        <div className="add-entity-endAdornment-wrapper">
                          <span className="add-entity-endAdornment">
                            Loading...
                          </span>
                        </div>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            filterOptions={filterIOOptions}
            freeSolo={false}
          />
        </div>
      )}

      {/* Ctag Field - Only visible for capital assets */}
      {viewCtag && (
        <div className="mb-3">
          {CAPITAL_ASSET_FIELDS.C_TAG} <span style={{ color: 'red' }}>*</span>
          <Form.Control
            type="text"
            placeholder="Enter CTag"
            value={ctag}
            onChange={(e) =>
              handleCtagChange(
                e.target.value.trim() === '' ? null : e.target.value.trim()
              )
            }
            readOnly={readOnlyCtag || !capitalAsset.isCapitalAsset}
          />
        </div>
      )}

      {/* Validation Message for PO, IO, and Ctag Selection */}
      {(!readOnlyPOIO || !readOnlyCtag) &&
        capitalAsset.isCapitalAsset &&
        !areRequiredFieldsFilled && (
          <div className="mb-3 p-3 add-entity-validation-message">
            <small className="text-muted">
              PO Number, IO Number, and Ctag are required for capital assets.
            </small>
          </div>
        )}

      {errorModal ? (
        <Modal
          show={errorModal}
          onHide={() => closeErrorPopup()}
          backdrop="static"
          keyboard={false}
        >
          <ModalBody>
            <p>
              Capital Asset information is incomplete. If you choose to close
              this popup, none of the capital asset details will be saved.
            </p>
            <br></br>{' '}
            <p>
              {' '}
              Are you sure you want to cancel update to capital asset
              information?
            </p>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => skipCapitalUpdate()}>Yes</Button>
            <Button onClick={() => closeErrorPopup()}>No</Button>
          </ModalFooter>
        </Modal>
      ) : null}
    </>
  );
};

export default CapitalAssetSelector;
